import { createAccessControl } from 'better-auth/plugins/access';

// Client-side mirror of packages/auth/src/access-control.ts. KEEP IN SYNC.
// Drives hasPermission() in route guards and components.
const statements = {
  organization: ['update', 'delete'],
  billing: ['update'],
  member: ['create', 'update', 'delete'],
  invitation: ['create', 'cancel'],
  team: ['create', 'update', 'delete'],
  ac: ['create', 'read', 'update', 'delete'],
  project: ['create', 'update', 'delete'],
  action: ['create', 'delete'],
  apiKey: ['create', 'read', 'update', 'delete'],
  reports: ['read'],
  identities: ['read'],
} as const;

export const ac = createAccessControl(statements);

/** Full permissions on all resources. */
const ownerRole = ac.newRole({
  organization: ['update', 'delete'],
  billing: ['update'],
  member: ['create', 'update', 'delete'],
  invitation: ['create', 'cancel'],
  team: ['create', 'update', 'delete'],
  ac: ['create', 'read', 'update', 'delete'],
  project: ['create', 'update', 'delete'],
  action: ['create', 'delete'],
  apiKey: ['create', 'read', 'update', 'delete'],
  reports: ['read'],
  identities: ['read'],
});

/** Can manage members, invitations, update the org (not delete or billing), manage projects and actions. */
const adminRole = ac.newRole({
  organization: ['update'],
  member: ['create', 'update', 'delete'],
  invitation: ['create', 'cancel'],
  team: ['create', 'update', 'delete'],
  ac: ['read'],
  project: ['create', 'update'],
  action: ['create', 'delete'],
  apiKey: ['create', 'read', 'update', 'delete'],
  reports: ['read'],
  identities: ['read'],
});

/** Read-only: may read reports surfaces and identities, never write. */
const limitedRole = ac.newRole({
  ac: ['read'],
  reports: ['read'],
  identities: ['read'],
});

export const roles = {
  owner: ownerRole,
  admin: adminRole,
  limited: limitedRole,
} as const;

type PermissionRequest = {
  [K in keyof typeof statements]?: readonly (typeof statements)[K][number][];
};

// Whether an org role holds the given permissions. Pure/sync, safe in route
// guards (beforeLoad) and components. Example: hasPermission('admin', { project: ['create'] }).
export function hasPermission(
  role: string | undefined,
  permissions: PermissionRequest,
): boolean {
  if (!role) return false;
  const roleObj = roles[role as keyof typeof roles] as
    | { authorize: (p: unknown) => { success: boolean } }
    | undefined;
  if (!roleObj) return false;
  return roleObj.authorize(permissions).success;
}
